import { jsPDF } from "jspdf";
import type { Event, EventAssignment } from "@satyrsmc/shared/client";
import { EventAssignmentCategory } from "@satyrsmc/shared/client";
import { formatDateLong } from "@/lib/date-utils";
import { EVENT_ASSIGNMENT_CATEGORY_LABELS as CATEGORY_LABELS } from "@/lib/event-constants";

/** Blank write-in lines printed under every role — whether or not it already
 * has names attached — so admins can pencil in a last-minute helper by hand
 * on the printed sheet. Nothing here is saved; it's just space on the page. */
const WRITE_IN_LINES = 2;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** Export the current assignments list as a simple text PDF, grouped by
 * category then role, with each role's existing assignees listed followed by
 * a couple of blank ruled lines for write-ins. */
export function downloadAssignmentsPdf(event: Event): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = 612;
  const pageHeight = 792;
  const margin = 54;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 14;

  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(event.name, margin, y);
  y += lineHeight + 4;

  if (event.start_date) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(formatDateLong(event.start_date), margin, y);
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 8;
  } else {
    y += 8;
  }

  const assignments = event.assignments ?? [];
  const categories = [EventAssignmentCategory.Planning, EventAssignmentCategory.During];

  for (const category of categories) {
    const roles = assignments
      .filter((a) => a.category === category)
      .sort((a: EventAssignment, b: EventAssignment) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    if (roles.length === 0) continue;

    ensureSpace(lineHeight * 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(CATEGORY_LABELS[category], margin, y);
    y += lineHeight + 4;

    for (const role of roles) {
      const memberNames = (role.members ?? [])
        .map((m) => m.member?.name)
        .filter((name): name is string => !!name);

      ensureSpace(lineHeight * 2);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(role.name, margin, y);
      y += lineHeight;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      for (const name of memberNames) {
        const wrapped = doc.splitTextToSize(`•  ${name}`, contentWidth - 14);
        ensureSpace(lineHeight * wrapped.length);
        doc.text(wrapped, margin + 14, y);
        y += lineHeight * wrapped.length;
      }

      // Blank write-in lines — always present, even for roles with existing
      // assignees, so an admin can pencil in one more name by hand.
      doc.setDrawColor(180, 180, 180);
      for (let i = 0; i < WRITE_IN_LINES; i++) {
        ensureSpace(lineHeight);
        const ruleY = y - 4;
        doc.line(margin + 14, ruleY, margin + 14 + (contentWidth - 14), ruleY);
        y += lineHeight;
      }
      doc.setDrawColor(0, 0, 0);

      y += 6;
    }
    y += 8;
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = slugify(event.name);
  a.download = slug ? `assignments-${slug}.pdf` : "assignments.pdf";
  a.click();
  URL.revokeObjectURL(url);
}
